import { getRolesFromCache, isAdminRole } from "@/api/roles";
import { useIsAssumingRole } from "@/hooks/useIsAssumingRole";
import { devicePreviewCommand } from "@/modules/command-palette/device-preview";
import { idSearchCommands } from "@/modules/command-palette/id-search";
import {
  mockSupplierOnboardingCommand,
  mockSupporterOnboardingCommand,
} from "@/modules/command-palette/mock-onboarding";
import { mockPlanCommand } from "@/modules/command-palette/mock-plan";
import { searchMembersCommand } from "@/modules/command-palette/search-members";
import { searchSuppliersCommand } from "@/modules/command-palette/search-suppliers";
import { searchSupportersCommand } from "@/modules/command-palette/search-supporters";
import { addCommands, addRichSearchResults, CommandPalette } from "@givenwell/command-palette";
import { Box, Flex } from "@givenwell/components";
import { colors } from "@givenwell/theme";
import { createContext, ReactNode, useEffect } from "react";
import { FixedSidebar } from "./FixedSidebar";

export function AppLayout({
  sidebar,
  role,
  children,
}: {
  sidebar?: ReactNode;
  role: "admin" | "supporter" | "supplier";
  children?: ReactNode;
}) {
  const assuming = useIsAssumingRole(role);
  const isAdminUser = getRolesFromCache()?.roles.some(isAdminRole);

  useEffect(() => {
    if (isAdminUser) {
      return addCommands([
        searchMembersCommand,
        searchSuppliersCommand,
        searchSupportersCommand,
        devicePreviewCommand,
        mockSupporterOnboardingCommand,
        mockSupplierOnboardingCommand,
      ]);
    }
  }, [assuming, role]);
  useEffect(() => {
    if (isAdminUser) {
      return addRichSearchResults(idSearchCommands);
    }
  }, [assuming, role]);

  useEffect(() => {
    if (isAdminUser && role === "supporter") {
      return addCommands([mockPlanCommand]);
    }
  }, [assuming, role]);

  return (
    <Box
      css={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        backgroundColor: colors.warmGreyHalf,
      }}
    >
      <FixedSidebar>{sidebar}</FixedSidebar>
      <Flex
        css={{
          width: "100%",
          minH: "100%",
          maxH: "100%",
        }}
      >
        <Flex
          css={{
            flexDir: "column",
            w: "100%",
            overflowX: "hidden",
            gap: 16,
            paddingLeft: 0,
            paddingBottom: 0,
            paddingRight: 0,
            paddingTop: "calc(var(--safe-area-inset-top))",
            "@md": {
              paddingBottom: 8,
              paddingRight: 8,
              paddingTop: "max(var(--safe-area-inset-top), 8px)",
              paddingLeft: 230,
            },
          }}
        >
          <SidebarContext value={sidebar}>{children}</SidebarContext>
        </Flex>
      </Flex>
      <CommandPalette />
    </Box>
  );
}

export const SidebarContext = createContext<ReactNode>(null);
